// 施工実施セッション(新規/再開)。mock 準拠の外側ステップタブ構成:
//   施工箇所選択 › 施工情報入力 › 攪拌実施 › 作業実施 (+完了ダイアログで品質測定)
// 攪拌/作業は 1 件 = 1 サブタブのパネル編集。ヘッダーに「完了して保存 / 未完了のまま保存」。
// データは web の実機構(putRow/patchRow → reloadCaching でサーバ追従)。Construction は
// 最初の実績登録まで遅延作成し、construction_plan_id は選択した施工箇所から導出する。

import { useState, useMemo, useEffect, useRef, Fragment } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Stack, Typography, Paper, Box, Button, TextField,
  Chip, Tabs, Tab, Snackbar
} from "@mui/material";
import EngineeringIcon from "@mui/icons-material/Engineering";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import AddIcon from "@mui/icons-material/Add";
import { PageContext } from "../PageContext";
import { ContentLoading } from "../../../components/Loading";
import { useFullDb } from "../../../api/dbSlice";
import { EntityWriteKey } from "../../../api/Api";
import { formatHMS } from "../../../util/dateFormat";
import Time from "../../../util/Time";
import type { StirBatchBase } from "../../../schema/generated/StirBatch";
import { epochToDateInput, dateInputToEpoch } from "../../../util/dateInput";
import { computeElapsedSec, mixInfoForWorkDetail, PauseMap } from "./helpers";
import { deriveMixedAt } from "../../../util/constructionStatus";
import { SectionCard, StepStatus, STEP_STATUS_META } from "./SectionCard";
import { useTimerNow } from "./TimerControl";
import { ProcessExecutionTab } from "./ProcessExecutionTab";
import { StirBatchTab } from "./StirBatchTab";
import { ConstructionActionTab } from "./ConstructionActionTab";
import { CompletionDialog } from "./CompletionDialog";
import { errorMessageOf } from "../../../util/typed";
import { useMountSync } from "../../../util/useMountSync";
import { rangesFromIds } from "../../../util/Materialperser";

type Mode = "new" | "resume";

type OuterTab = "process" | "basic" | "stir" | "action";

// 外側ステップタブの見出し(タイトル + 件数 + 状態バッジ)。
function OuterTabLabel({ title, count, status }: { title: string; count?: number; status: StepStatus }) {
  const meta = STEP_STATUS_META[status];
  return (
    <Stack direction="row" alignItems="center" gap={0.75} justifyContent="center">
      <Typography variant="body2" fontWeight={600}>{title}</Typography>
      {count != null && <Chip size="small" variant="outlined" label={count} sx={{ pointerEvents: "none" }} />}
      <Chip size="small" color={meta.color} label={meta.label}
        sx={{ height: 18, fontSize: 10, pointerEvents: "none", "& .MuiChip-label": { px: 0.75 } }} />
    </Stack>
  );
}

// サブタブ(攪拌#N / 作業#N)の見出し。完了=「完」/実行中=再生/未完了=「未」。
function SubTabLabel({ title, done, running }: { title: string; done: boolean; running: boolean }) {
  const renderBadge = () => {
    if (done) {
      return <Chip size="small" color="success" label="完" sx={{ height: 16, fontSize: 9, "& .MuiChip-label": { px: 0.5 } }} />;
    }
    if (running) {
      return <PlayArrowIcon fontSize="small" color="success" />;
    }
    return <Chip size="small" color="warning" label="未" sx={{ height: 16, fontSize: 9, "& .MuiChip-label": { px: 0.5 } }} />;
  };
  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      <span>{title}</span>
      {renderBadge()}
    </Stack>
  );
}

export default function ExecuteSessionPage({ pageContext, mode }: { pageContext: PageContext; mode: Mode }) {
  const { id: paramId } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { db, loading, reload } = useFullDb(pageContext.api.indexedDB, pageContext.api.currentUser.id);

  // 編集(resume)時はローカルIDBから既存施工+子テーブルを読むため、表示前にサーバ同期する。
  // 同期完了(syncing=false)まで操作不可にする: 未完了のうちに施工情報の hydrate effect が
  // 走ると、同期後の reload で入力済みの値が上書きされてしまうため。
  const syncing = useMountSync(mode === "resume" && pageContext.api.isOnline, () => pageContext.api.entities.syncConstructionData(), reload);

  const [constructionId, setConstructionId] = useState<string | null>(mode === "resume" ? (paramId ?? null) : null);
  const [outerTab, setOuterTab] = useState<OuterTab>("basic");
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const ensureRef = useRef<Promise<string> | null>(null);
  const [completeOpen, setCompleteOpen] = useState(false);

  // URL クエリ: area_id(+processes=all / work_detail_id)は開始ボタンからの事前投入。
  const queryAreaId = searchParams.get("area_id");
  const queryWorkDetailId = searchParams.get("work_detail_id");
  const queryAllProcesses = searchParams.get("processes") === "all";

  // ===== 施工情報(Construction 直書き項目) =====
  const existingCon = useMemo(() => {
    if (constructionId == null) return null;
    return db.constructions.find((c) => c.id === constructionId) ?? null;
  }, [db.constructions, constructionId]);

  const [planId, setPlanId] = useState<string>("");
  const [envExecutedAt, setEnvExecutedAt] = useState<string>("");
  const [envTemp, setEnvTemp] = useState<string>("");
  const [envHumidity, setEnvHumidity] = useState<string>("");
  const [envRemarks, setEnvRemarks] = useState<string>("");
  // 攪拌実施を行うか(mock 準拠のトグル)。false なら攪拌 0 件でも完了できる。
  const [hasStir, setHasStir] = useState(true);

  // existingCon は reload() のたびに参照が変わるため、対象セッション(id)ごとに一度だけ hydrate する。
  // ガードが無いと子操作の reload() 再発火のたびに入力中の値がサーバ側の旧値へ巻き戻ってしまう。
  const hydratedConIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (existingCon) {
      if (hydratedConIdRef.current === existingCon.id) {
        return;
      }
      hydratedConIdRef.current = existingCon.id;
      setPlanId(existingCon.construction_plan_id);
      setEnvExecutedAt(epochToDateInput(existingCon.executed_at));
      setEnvTemp(existingCon.ambient_temperature == null ? "" : String(existingCon.ambient_temperature));
      setEnvHumidity(existingCon.ambient_humidity == null ? "" : String(existingCon.ambient_humidity));
      setEnvRemarks(existingCon.remarks ?? "");
      setHasStir(existingCon.has_stir_execution !== false);
    } else if (mode === "new" && queryAreaId != null) {
      // 開始ボタン経由: 施工箇所から施工計画を導出する。
      const hit = db.constructionPlanAreas.find((a) => a.id === queryAreaId);
      if (hit) {
        setPlanId(hit.construction_plan_id);
      }
    }
  }, [existingCon, mode, db.constructionPlanAreas, queryAreaId]);

  // ===== 実績行 =====
  const pes = useMemo(
    () => db.processExecutions.filter((p) => p.construction_id === constructionId && p.deleted_at == null),
    [db.processExecutions, constructionId]
  );
  const stirs = useMemo(
    () => db.stirBatches.filter((s) => s.construction_id === constructionId && s.deleted_at == null).sort((a, b) => a.sort_order - b.sort_order),
    [db.stirBatches, constructionId]
  );
  const actions = useMemo(
    () => db.constructionActions.filter((a) => a.construction_id === constructionId && a.deleted_at == null).sort((a, b) => a.sort_order - b.sort_order),
    [db.constructionActions, constructionId]
  );
  const consumptions = useMemo(
    () => db.constructionMaterialConsumptions.filter((c) => c.construction_id === constructionId && c.deleted_at == null),
    [db.constructionMaterialConsumptions, constructionId]
  );

  // 材料QRはログイン時同期の対象外で、検索・スキャン時の SetCache でしかローカルキャッシュされない。
  // resume したセッションのエントリQRが未キャッシュだと LotNo・kg換算の表示が欠落するため、
  // 欠落分をサーバから一度だけ補充する(getFromIdSearch が QR と Lot を SetCache する)。
  const missingQrFetchedRef = useRef(false);
  useEffect(() => {
    if (loading || missingQrFetchedRef.current || !pageContext.api.isOnline) {
      return;
    }
    const cachedQrIds = new Set(db.materialQrs.map((q) => q.id));
    const missingIds = Array.from(new Set(consumptions.map((c) => c.material_qr_id))).filter((id) => !cachedQrIds.has(id));
    if (missingIds.length === 0) {
      return;
    }
    missingQrFetchedRef.current = true;
    pageContext.api.material.getFromIdSearch({ targetRanges: rangesFromIds(missingIds) })
      .then(() => reload())
      .catch((e) => console.warn("エントリQRのキャッシュ補充に失敗しました", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, consumptions, db.materialQrs]);

  // タイマーの一時停止状態(クライアントのみ)は攪拌/作業共有でページが保持する。
  const [pauseMap, setPauseMap] = useState<PauseMap>({});
  const isRowRunning = (r: { id: string; started_at: number | null; finished_at: number | null }) => {
    const pause = pauseMap[r.id];
    return r.started_at != null && r.finished_at == null && (pause == null || pause.pausedAt == null);
  };
  const hasRunning = stirs.some(isRowRunning) || actions.some(isRowRunning);
  const now = useTimerNow(hasRunning);

  // 再開ロード時の paused_total_ms 逆算(A-4-5)。pauseMap はクライアントのみでリロードで揮発するため、
  // 一時停止時に patch した duration_seconds(チェックポイント)から、実開始時刻との差分を停止時間とみなして復元する。
  // 初期化済みの行には触れない(実行中に別画面から戻ってきた場合の二重初期化を避ける)。
  // 制約: 一時停止の再開状態(pausedAt)はサーバ列を持たず永続化されない。そのためリロード後は
  // 「最後のチェックポイント時点で一時停止していた」状態として復元され、実際に停止していた時間の
  // 水増しはしない方向の割り切りである。恒久対応が必要な場合は paused_at 相当のスキーマ列追加が要る。
  const pauseInitRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const nowMs = Date.now();
    const toInit: { id: string; pausedTotalMs: number }[] = [];
    const rows: { id: string; started_at: number | null; finished_at: number | null; duration_seconds: number | null }[] = [...stirs, ...actions];
    for (const r of rows) {
      if (r.started_at == null || r.finished_at != null) {
        continue;
      }
      if (pauseInitRef.current.has(r.id) || pauseMap[r.id] != null) {
        continue;
      }
      pauseInitRef.current.add(r.id);
      if (r.duration_seconds == null) {
        continue;
      }
      const pausedTotalMs = Math.max(0, (nowMs - r.started_at) - r.duration_seconds * 1000);
      if (pausedTotalMs > 0) {
        toInit.push({ id: r.id, pausedTotalMs });
      }
    }
    if (toInit.length > 0) {
      setPauseMap((prev) => {
        const next = { ...prev };
        for (const t of toInit) {
          next[t.id] = { pausedTotalMs: t.pausedTotalMs, pausedAt: nowMs };
        }
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stirs, actions]);

  // サブタブ選択("new" = 追加パネル)。
  const [activeStirId, setActiveStirId] = useState<string | null>(null);
  const [activeActionId, setActiveActionId] = useState<string | null>(null);
  useEffect(() => {
    if (activeStirId == null) {
      const firstStirRow = stirs[0];
      if (firstStirRow !== undefined) {
        setActiveStirId(firstStirRow.id);
      }
      return;
    }
    if (activeStirId !== "new" && !stirs.some((s) => s.id === activeStirId)) {
      setActiveStirId(stirs[0]?.id ?? null);
    }
  }, [stirs, activeStirId]);
  useEffect(() => {
    if (activeActionId == null) {
      const firstActionRow = actions[0];
      if (firstActionRow !== undefined) {
        setActiveActionId(firstActionRow.id);
      }
      return;
    }
    if (activeActionId !== "new" && !actions.some((a) => a.id === activeActionId)) {
      setActiveActionId(actions[0]?.id ?? null);
    }
  }, [actions, activeActionId]);

  const reloadCaching = (names: EntityWriteKey[]) => async () => {
    if (pageContext.api.isOnline) {
      for (const n of names) {
        try {
          await pageContext.api.entities.getListAndCache(n);
        } catch (e) {
          console.warn(`getListAndCache failed: ${n}`, e);
        }
      }
    }
    await reload();
  };

  const envCreateValues = () => ({
    executed_at: dateInputToEpoch(envExecutedAt),
    ambient_temperature: envTemp === "" ? null : Number(envTemp),
    ambient_humidity: envHumidity === "" ? null : Number(envHumidity),
    remarks: envRemarks === "" ? null : envRemarks,
    has_stir_execution: hasStir,
  });
  const envPatchValues = () => ({
    construction_plan_id: planId,
    ...envCreateValues(),
  });

  // 遅延作成: 実 Construction の生成は子エンティティ書込の直前まで遅延する(中断時はサーバ未登録)。
  // 施工計画は選択済み planId か、施工箇所からの導出値(planOverride)を使う。作成時は必ず下書き。
  const ensureConstruction = (planOverride?: string): Promise<string> => {
    if (constructionId != null) {
      return Promise.resolve(constructionId);
    }
    if (ensureRef.current != null) {
      return ensureRef.current;
    }
    const effectivePlan = planId !== "" ? planId : (planOverride ?? "");
    if (effectivePlan === "") {
      pageContext.api.showError("施工箇所を先に選択してください(施工計画を導出できません)。");
      return Promise.reject(new Error("construction_plan_id is not resolved"));
    }
    const creating = (async () => {
      try {
        const res = await pageContext.api.construction.put({ construction_plan_id: effectivePlan, ...envCreateValues(), finished_at: null });
        // 作成直後は今まさに入力中の環境値でサーバへ登録しているため、reload で existingCon が
        // 初出現しても hydrate effect による上書きは不要(むしろ有害)。先に hydrate 済み扱いにしておく。
        hydratedConIdRef.current = res.id;
        setConstructionId(res.id);
        if (planId === "") {
          setPlanId(effectivePlan);
        }
        await reload();
        return res.id;
      } catch (e) {
        // 失敗時は次回呼び出しで再作成できるよう ensureRef を戻し、呼び出し元へエラーを伝える(以後の保存全滅を防ぐ)。
        ensureRef.current = null;
        throw e;
      }
    })();
    ensureRef.current = creating;
    return creating;
  };

  // 消費済みQR → 材料ID(品質項目の material スコープ判定用。materialQrs 未同期環境では空になる)。
  const consumedMaterialIds = useMemo(() => {
    const ids = new Set<number>();
    for (const c of consumptions) {
      const qr = db.materialQrs.find((q) => q.id === c.material_qr_id);
      const lot = qr?.lot_id != null ? db.lots.find((l) => l.id === qr.lot_id) : null;
      if (lot?.material_id != null) {
        ids.add(lot.material_id);
      }
    }
    return ids;
  }, [consumptions, db.materialQrs, db.lots]);

  // 攪拌の主材料が qr_managed かどうか(mixInfoForWorkDetail 経由。配合比未設定なら null)。
  const stirMainMaterialQrManaged = (s: StirBatchBase): boolean => {
    const info = mixInfoForWorkDetail(db, s.work_detail_id);
    return info?.mainMaterial?.qr_managed === true;
  };

  // 攪拌の消費QR件数(materialQrs 未同期でも constructionMaterialConsumptions の件数だけで判定できる)。
  const stirConsumptionCount = (s: StirBatchBase): number =>
    consumptions.filter((c) => c.stir_batch_id === s.id).length;

  // 完了を阻む条件(mock isAllComplete 準拠)。品質の必須入力は完了ダイアログ側で検証する。
  const getCompletionIssues = (): string[] => {
    const issues: string[] = [];
    if (constructionId == null && pes.length === 0) {
      issues.push("施工箇所と工程を選択してください");
    }
    if (envExecutedAt === "") {
      issues.push("施工日を入力してください");
    }
    if (envTemp === "" || envHumidity === "") {
      issues.push("気温・湿度を入力してください");
    }
    if (constructionId != null && pes.length === 0) {
      issues.push("工程実施を1件以上登録してください");
    }
    if (hasStir && stirs.length === 0) {
      issues.push("攪拌実施を1件以上登録してください(行わない場合は攪拌タブでオフ)");
    }
    if (hasStir && stirs.some((s) => s.finished_at == null)) {
      issues.push("未完了の攪拌があります(タイマーを完了してください)");
    }
    if (hasStir && stirs.some((s) => stirMainMaterialQrManaged(s) && stirConsumptionCount(s) === 0)) {
      issues.push("QR管理の主材料を使う攪拌で、材料のQRコードが1件も消費されていません");
    }
    if (actions.length === 0) {
      issues.push("作業実施を1件以上登録してください");
    }
    if (actions.some((a) => a.finished_at == null)) {
      issues.push("未完了の作業があります(タイマーを完了してください)");
    }
    return issues;
  };

  // 未完了(下書き)のまま保存 → 一覧の施工実施タブへ戻る(mock backToTab)。
  const finalizeDraft = async () => {
    if (envExecutedAt === "") {
      pageContext.api.showError("施工日を入力してください(これだけは必須)。");
      return;
    }
    setSaving(true);
    try {
      const cid = await ensureConstruction();
      // 未完了(下書き)保存 = finished_at を null に戻す(完了済みを再編集した場合も下書きへ戻る)。
      await pageContext.api.construction.patch({ id: cid, ...envPatchValues(), finished_at: null });
      setSnackbar("未完了(下書き)として保存しました");
      await reload();
      navigate("..", { state: { backToTab: "construction" } });
    } catch (e) {
      pageContext.api.showError(`保存に失敗しました: ${errorMessageOf(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const onCompleteClick = () => {
    const issues = getCompletionIssues();
    if (issues.length > 0) {
      pageContext.api.showError("完了できません:\n・" + issues.join("\n・"));
      return;
    }
    setCompleteOpen(true);
  };

  // ===== 事前投入(全工程を開始 / この工程を開始) =====
  // 開始ボタンからの進入は「作業開始の意思」なので、mock 同様ドラフト施工+工程実施を即登録する。
  const presetRef = useRef(false);
  useEffect(() => {
    if (mode !== "new" || loading || presetRef.current) {
      return;
    }
    if (queryAreaId == null || (!queryAllProcesses && queryWorkDetailId == null)) {
      return;
    }
    const area = db.constructionPlanAreas.find((a) => a.id === queryAreaId && a.deleted_at == null);
    if (area == null) {
      return;
    }
    presetRef.current = true;
    const wds = db.workDetails
      .filter((d) => d.deleted_at == null && d.work_subtype_id === area.work_subtype_id)
      .sort((a, b) => a.id - b.id);
    let targets = wds;
    if (!queryAllProcesses) {
      targets = wds.filter((d) => d.id === Number(queryWorkDetailId));
    } else {
      targets = wds.filter((d) => {
        const done = db.processExecutions.some(
          (p) => p.deleted_at == null && p.construction_plan_area_id === area.id && p.work_detail_id === d.id && p.completed_at != null
        );
        return !done;
      });
    }
    if (targets.length === 0) {
      return;
    }
    const run = async () => {
      setSaving(true);
      try {
        const cid = await ensureConstruction(area.construction_plan_id);
        for (const wd of targets) {
          await pageContext.api.entities.putRow("processExecutions", {
            construction_id: cid,
            construction_plan_area_id: area.id,
            work_detail_id: wd.id,
            completed_at: null,
            remarks: null,
          });
        }
        setSnackbar(`${targets.length} 件の工程実施を追加しました`);
        await reloadCaching(["processExecutions"])();
      } catch (e) {
        pageContext.api.showError(`工程実施の事前登録に失敗しました: ${errorMessageOf(e)}`);
      } finally {
        setSaving(false);
      }
    };
    run();
  }, [
    mode, loading, queryAreaId, queryAllProcesses, queryWorkDetailId,
    db.constructionPlanAreas, db.workDetails, db.processExecutions,
    ensureConstruction, reloadCaching, pageContext,
  ]);

  // ===== 各ステップの状態と件数 =====
  let basicStatus: StepStatus = "incomplete";
  if (envExecutedAt !== "" && envTemp !== "" && envHumidity !== "") {
    basicStatus = "done";
  } else if (envExecutedAt !== "") {
    basicStatus = "in_progress";
  }
  let processStatus: StepStatus = "incomplete";
  if (pes.length > 0 && pes.every((p) => p.completed_at != null)) {
    processStatus = "done";
  } else if (pes.length > 0) {
    processStatus = "in_progress";
  }
  // 攪拌1件の材料準備完了判定(mixInfoForWorkDetail 準拠)。QR管理外の主材料は消費0件でも完了扱い。
  const stirMaterialReady = (s: StirBatchBase): boolean =>
    s.process_execution_id != null && (!stirMainMaterialQrManaged(s) || stirConsumptionCount(s) > 0);
  let stirStatus: StepStatus = "incomplete";
  if (!hasStir) {
    stirStatus = "done";
  } else if (stirs.length > 0 && stirs.every((s) => s.finished_at != null && stirMaterialReady(s))) {
    stirStatus = "done";
  } else if (stirs.length > 0) {
    stirStatus = "in_progress";
  }
  let actionStatus: StepStatus = "incomplete";
  if (actions.length > 0 && actions.every((a) => a.finished_at != null)) {
    actionStatus = "done";
  } else if (actions.length > 0) {
    actionStatus = "in_progress";
  }
  const allOk = getCompletionIssues().length === 0;
  const dateOk = envExecutedAt !== "";

  const TAB_ORDER: OuterTab[] = ["process", "basic", "stir", "action"];
  const goNextTab = (from: OuterTab) => {
    const idx = TAB_ORDER.indexOf(from);
    const nextTab = TAB_ORDER[idx + 1];
    if (idx >= 0 && nextTab !== undefined) {
      setOuterTab(nextTab);
    }
  };

  // 右下の実行中タイマースタック(開始済み・未完了の攪拌/作業)。クリックで該当サブタブへ移動。
  const runningTimers = useMemo(() => {
    const out: { key: string; kind: "stir" | "action"; id: string; no: number; running: boolean; elapsed: number }[] = [];
    stirs.forEach((s, i) => {
      if (s.started_at != null && s.finished_at == null) {
        out.push({ key: `stir-${s.id}`, kind: "stir", id: s.id, no: i + 1, running: isRowRunning(s), elapsed: computeElapsedSec(s.started_at, null, pauseMap[s.id], now) });
      }
    });
    actions.forEach((a, i) => {
      if (a.started_at != null && a.finished_at == null) {
        out.push({ key: `action-${a.id}`, kind: "action", id: a.id, no: i + 1, running: isRowRunning(a), elapsed: computeElapsedSec(a.started_at, null, pauseMap[a.id], now) });
      }
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stirs, actions, pauseMap, now]);

  if (loading || syncing) {
    return (
      <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
          <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate(constructionId != null ? `../${constructionId}/` : "../")}>戻る</Button>
          <Typography variant="h5" fontWeight={700}>施工実施編集</Typography>
        </Stack>
        <ContentLoading />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      {/* ===== ヘッダー(戻る/タイトル/保存アクション) ===== */}
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate(constructionId != null ? `../${constructionId}/` : "../")}>戻る</Button>
        <EngineeringIcon color="action" />
        <Typography variant="h4" fontWeight="bold">施工実施編集</Typography>
        {constructionId != null && (
          <Chip size="small" variant="outlined" label={`施工実施 ${constructionId.slice(0, 8)}`} />
        )}
        <Box flex={1} />
        {allOk ? (
          <Button variant="contained" color="success" size="small" startIcon={<CheckCircleIcon />}
            disabled={saving} onClick={onCompleteClick}>完了して保存</Button>
        ) : (
          <Button variant="contained" color="warning" size="small"
            disabled={saving || !dateOk} onClick={finalizeDraft}>未完了のまま保存</Button>
        )}
      </Stack>

      {/* ===== 外側ステップタブ + 攪拌/作業サブタブ ===== */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          {([
            { value: "process", title: "施工箇所選択", count: pes.length, status: processStatus },
            { value: "basic", title: "施工情報入力", count: undefined, status: basicStatus },
            { value: "stir", title: "攪拌実施", count: stirs.length, status: stirStatus },
            { value: "action", title: "作業実施", count: actions.length, status: actionStatus },
          ] satisfies { value: OuterTab; title: string; count: number | undefined; status: StepStatus }[]).map((s, i) => (
            <Fragment key={s.value}>
              {i > 0 && <NavigateNextIcon fontSize="small" sx={{ color: "text.disabled" }} />}
              <Box onClick={() => setOuterTab(s.value)}
                sx={{ cursor: "pointer", px: 1, py: 0.5, borderRadius: 1,
                  bgcolor: outerTab === s.value ? "action.selected" : "transparent",
                  "&:hover": { bgcolor: "action.hover" } }}>
                <Box sx={{ color: outerTab === s.value ? "primary.main" : "text.secondary" }}>
                  <OuterTabLabel title={s.title} count={s.count} status={s.status} />
                </Box>
              </Box>
            </Fragment>
          ))}
        </Box>
        {outerTab === "stir" && stirs.length > 0 && (
          <Tabs value={activeStirId != null && (activeStirId === "new" || stirs.some((s) => s.id === activeStirId)) ? activeStirId : stirs[0]?.id ?? ""}
            onChange={(_, v) => setActiveStirId(v)}
            variant="scrollable" scrollButtons="auto" textColor="secondary" indicatorColor="secondary"
            sx={{ minHeight: 38, "& .MuiTab-root": { minHeight: 38, fontSize: 13, py: 1 } }}>
            {stirs.map((s, i) => (
              <Tab key={s.id} value={s.id}
                label={<SubTabLabel title={`攪拌#${i + 1}`} done={s.finished_at != null} running={isRowRunning(s)} />} />
            ))}
            <Tab value="new" disabled={pes.length === 0} icon={<AddIcon fontSize="small" />} iconPosition="start" label="追加" />
          </Tabs>
        )}
        {outerTab === "action" && actions.length > 0 && (
          <Tabs value={activeActionId != null && (activeActionId === "new" || actions.some((a) => a.id === activeActionId)) ? activeActionId : actions[0]?.id ?? ""}
            onChange={(_, v) => setActiveActionId(v)}
            variant="scrollable" scrollButtons="auto" textColor="secondary" indicatorColor="secondary"
            sx={{ minHeight: 38, "& .MuiTab-root": { minHeight: 38, fontSize: 13, py: 1 } }}>
            {actions.map((a, i) => (
              <Tab key={a.id} value={a.id}
                label={<SubTabLabel title={`作業#${i + 1}`} done={a.finished_at != null} running={isRowRunning(a)} />} />
            ))}
            <Tab value="new" disabled={pes.length === 0} icon={<AddIcon fontSize="small" />} iconPosition="start" label="追加" />
          </Tabs>
        )}
      </Paper>

      {/* ===== 施工情報入力 ===== */}
      {outerTab === "basic" && (
        <Stack gap={2}>
          <SectionCard title="施工情報" status={basicStatus}
            info="施工日(必須)と、当日の気温・湿度を入力します。値はヘッダーの保存(完了して保存 / 未完了のまま保存)で確定します。">
            <Stack gap={2} sx={{ maxWidth: 480 }}>
              <TextField type="date" label="施工日 *" size="small" InputLabelProps={{ shrink: true }}
                value={envExecutedAt} error={envExecutedAt === ""}
                onChange={(e) => setEnvExecutedAt(e.target.value)} />
              <Stack direction="row" gap={2}>
                <TextField type="number" label="気温 (℃)" size="small" fullWidth value={envTemp}
                  error={envTemp === ""} onChange={(e) => setEnvTemp(e.target.value)} />
                <TextField type="number" label="湿度 (%)" size="small" fullWidth value={envHumidity}
                  error={envHumidity === ""} onChange={(e) => setEnvHumidity(e.target.value)} />
              </Stack>
              <TextField label="備考" size="small" multiline minRows={2} value={envRemarks}
                onChange={(e) => setEnvRemarks(e.target.value)} placeholder="施工実施全体に対するメモ (任意)" />
              {existingCon && (() => {
                const mixedAt = deriveMixedAt(existingCon.id, stirs);
                return (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                    <Typography variant="subtitle2" fontWeight="bold" mb={1}>導出値(読み取り専用)</Typography>
                    <Stack gap={0.5}>
                      <Typography variant="body2">調合時刻(撹拌開始の最小値): {mixedAt != null ? new Time(mixedAt).Format("YYYY/MM/dd HH:mm:ss") : "-"}</Typography>
                      <Typography variant="body2">施工完了時刻(サーバ導出): {existingCon.finished_at != null ? new Time(existingCon.finished_at).Format("YYYY/MM/dd HH:mm:ss") : "-"}</Typography>
                    </Stack>
                  </Paper>
                );
              })()}
            </Stack>
          </SectionCard>
        </Stack>
      )}

      {/* ===== 施工箇所選択(工程実施) ===== */}
      {outerTab === "process" && (
        <Stack gap={2}>
          <SectionCard title="施工箇所と工程" status={processStatus}
            errors={pes.length === 0 ? ["工程実施を追加してください"] : []}
            info="今回施工する施工箇所と工程を「工程実施を追加」から登録します。施工箇所も工程も複数選べます。">
            <ProcessExecutionTab
              pageContext={pageContext}
              db={db}
              constructionId={constructionId}
              ensureConstruction={ensureConstruction}
              planId={planId}
              reload={reloadCaching(["processExecutions"])}
              setSnackbar={setSnackbar}
            />
          </SectionCard>
        </Stack>
      )}

      {/* ===== 攪拌実施 ===== */}
      {outerTab === "stir" && (
        <StirBatchTab
          pageContext={pageContext}
          db={db}
          pes={pes}
          stirs={stirs}
          consumptions={consumptions}
          activeStirId={activeStirId}
          setActiveStirId={setActiveStirId}
          hasStir={hasStir}
          setHasStir={setHasStir}
          stirStatus={stirStatus}
          pauseMap={pauseMap}
          setPauseMap={setPauseMap}
          now={now}
          ensureConstruction={ensureConstruction}
          reload={reloadCaching(["stirBatches", "constructionMaterialConsumptions"])}
          setSnackbar={setSnackbar}
        />
      )}

      {/* ===== 作業実施 ===== */}
      {outerTab === "action" && (
        <ConstructionActionTab
          pageContext={pageContext}
          db={db}
          pes={pes}
          actions={actions}
          activeActionId={activeActionId}
          setActiveActionId={setActiveActionId}
          actionStatus={actionStatus}
          pauseMap={pauseMap}
          setPauseMap={setPauseMap}
          now={now}
          ensureConstruction={ensureConstruction}
          reload={reloadCaching(["constructionActions"])}
          setSnackbar={setSnackbar}
        />
      )}

      {/* ===== タブ共通: 次のステップ / 完了して保存 ===== */}
      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        {outerTab === "process" && (
          <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />}
            sx={{ minWidth: 260, py: 1.5, fontSize: 16 }}
            disabled={processStatus === "incomplete" && pes.length === 0} onClick={() => goNextTab("process")}>次のステップ →</Button>
        )}
        {outerTab === "basic" && (
          <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />}
            sx={{ minWidth: 260, py: 1.5, fontSize: 16 }}
            disabled={basicStatus === "incomplete"} onClick={() => goNextTab("basic")}>次のステップ →</Button>
        )}
        {outerTab === "stir" && (
          <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />}
            sx={{ minWidth: 260, py: 1.5, fontSize: 16 }}
            disabled={stirStatus === "incomplete"} onClick={() => goNextTab("stir")}>次のステップ →</Button>
        )}
        {outerTab === "action" && (
          <Button variant="contained" size="large" color="success" startIcon={<CheckCircleIcon />}
            sx={{ minWidth: 260, py: 1.5, fontSize: 16 }}
            disabled={!allOk || saving} onClick={onCompleteClick}>完了して保存</Button>
        )}
      </Box>

      {completeOpen && constructionId != null && (
        <CompletionDialog
          pageContext={pageContext}
          db={db}
          constructionId={constructionId}
          consumedMaterialIds={consumedMaterialIds}
          envPatch={envPatchValues()}
          onClose={() => setCompleteOpen(false)}
          onCompleted={() => { setCompleteOpen(false); navigate("..", { state: { backToTab: "construction" } }); }}
          reload={reloadCaching(["processExecutions", "qualityRecords"])}
          setSnackbar={setSnackbar}
        />
      )}

      {/* 実行中タイマーを右下に固定表示。クリックで該当サブタブへ移動する。 */}
      {runningTimers.length > 0 && (
        <Box sx={{ position: "fixed", bottom: 16, right: 16, zIndex: 1200, display: "flex", flexDirection: "column", gap: 1, alignItems: "stretch", width: 220 }}>
          <Typography variant="caption" sx={{ textAlign: "right", fontWeight: 700, color: "text.secondary" }}>実行中タイマー</Typography>
          {runningTimers.map((t) => (
            <Paper key={t.key} elevation={6}
              onClick={() => {
                if (t.kind === "stir") {
                  setOuterTab("stir");
                  setActiveStirId(t.id);
                } else {
                  setOuterTab("action");
                  setActiveActionId(t.id);
                }
              }}
              sx={{ cursor: "pointer", px: 1.5, py: 1, display: "flex", alignItems: "center", gap: 1,
                borderLeft: 5, borderLeftColor: t.running ? "success.main" : "warning.main", "&:hover": { boxShadow: 10 } }}>
              {t.running ? <PlayArrowIcon color="success" fontSize="small" /> : <PauseIcon color="warning" fontSize="small" />}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {t.kind === "stir" ? "攪拌実施" : "作業実施"} #{t.no} ・ {t.running ? "計測中" : "停止中"}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, lineHeight: 1.2 }}>{formatHMS(t.elapsed)}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar ?? ""}
      />
    </Box>
  );
}
